import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Card, CardContent, CardHeader, Grid, MenuItem, TextField, Button, Stack, Snackbar, Alert } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useTranslation } from 'react-i18next';
import { fetchJson } from '../../utils/fetchJson';
import { useNavigate } from 'react-router-dom';
import useScopeAutoRefresh from '../../hooks/useScopeAutoRefresh';
import getScopeHeaders, { activeSchoolId, activePhaseCode, activeAcademicYearId } from '../../utils/scopeHeaders';

const AssignedSubjectsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [classes, setClasses] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState({ open: false, severity: 'success', msg: '' });

  const notify = (severity, msg) => setSnack({ open: true, severity, msg });

  const loadClasses = useCallback(async () => {
    try {
      const cls = await fetchJson('/teacher/classes');
      setClasses(cls || []);
    } catch (e) {
      console.error(e);
      notify('error', t('assignedSubjectsPage.errors.loadClasses'));
    }
  }, [t]);

  useEffect(() => { loadClasses(); }, [loadClasses]);

  // THE YEAR SWITCHER HAS TO REACH THIS PAGE.
  //
  // Every request below is scoped by academic year — the allocations query
  // sends it as a query parameter AND as X-Academic-Year-Id — but nothing here
  // listened, so switching the year left the previous session's allocations on
  // screen under the new year's heading. The class list is reset with it,
  // because a class the teacher held last year may not exist in this one.
  useScopeAutoRefresh([() => { setSelectedClassId(''); setRows([]); loadClasses(); }], [loadClasses]);

  const loadAssignments = async (classId) => {
    if (!classId) { setRows([]); return; }
    setLoading(true);
    try {
      // The query string repeats what the headers already carry, so both halves
      // must come from the same resolution or the request contradicts itself —
      // this block read the school id as `schoolId` first while fetchJson reads
      // `activeSchoolId` first, and the academic year hangs off that choice.
      const headers = getScopeHeaders();
      const schoolId = activeSchoolId();
      const phaseCode = activePhaseCode();
      const ayId = activeAcademicYearId();
      // Fetch class allocations (subjects running in this class)
      const classSubs = await fetchJson(`/class-subjects?classId=${classId}&academicYearId=${encodeURIComponent(ayId)}&phase=${encodeURIComponent(phaseCode)}&schoolId=${encodeURIComponent(schoolId)}`, { headers });
      // Fetch teacher allocations (subjects this teacher can teach)
      const teacherSubs = await fetchJson(`/teacher-subjects/mine`);

      const teacherSubjectIds = new Set((teacherSubs || []).map(ts => ts.subjectId));

      const filtered = (classSubs || []).filter(cs => teacherSubjectIds.has(cs.subjectId));
      const mapped = filtered.map(cs => ({
        id: cs.id,
        className: cs.Class?.name || cs.class?.name || cs.className || '—',
        subjectName: cs.Subject?.name || cs.subject?.name || cs.subjectName || '—',
        subjectId: cs.subjectId,
        classId: cs.classId,
      }));
      setRows(mapped);
    } catch (e) {
      console.error(e);
      notify('error', t('assignedSubjectsPage.errors.loadAssignments'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAssignments(selectedClassId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClassId]);

  const columns = useMemo(() => [
    { field: 'className', headerName: t('assignedSubjectsPage.columns.class'), flex: 1 },
    { field: 'subjectName', headerName: t('assignedSubjectsPage.columns.subject'), flex: 1 },
    {
      field: 'actions',
      headerName: t('assignedSubjectsPage.columns.actions'),
      width: 180,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={1}>
          <Button size="small" variant="contained" onClick={() => navigate(`/teacher/marks?classSubjectId=${params.row.id}`)}>
            {t('assignedSubjectsPage.actions.recordMarks')}
          </Button>
        </Stack>
      ),
    },
  ], [navigate, t]);

  return (
    <Box p={2}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Card sx={{ borderRadius: 3 }}>
            <CardHeader title={t('assignedSubjectsPage.title')} subheader={t('assignedSubjectsPage.subtitle')} />
            <CardContent>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" sx={{ mb: 2 }}>
                <TextField select label={t('assignedSubjectsPage.selectClass')} value={selectedClassId} onChange={(e) => setSelectedClassId(e.target.value)} sx={{ minWidth: 240 }}>
                  {(classes || []).map((c) => (
                    <MenuItem key={c.id} value={c.id}>{c.name || c.label}</MenuItem>
                  ))}
                </TextField>
              </Stack>
              <div style={{ height: 420, width: '100%' }}>
                <DataGrid rows={rows} columns={columns} loading={loading} pageSizeOptions={[5, 10, 20]} initialState={{ pagination: { paginationModel: { pageSize: 10 } } }} disableRowSelectionOnClick />
              </div>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={() => setSnack(s => ({ ...s, open: false }))}>
        <Alert onClose={() => setSnack(s => ({ ...s, open: false }))} severity={snack.severity} sx={{ width: '100%' }}>
          {snack.msg}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default AssignedSubjectsPage;
